import { z } from 'zod';
import { LIMITS, documentIdSchema, nameSchema, timestampSchema } from './common';
import { courseSchema } from './course';
import { environmentSchema } from './environment';
import { vehicleSchema } from './vehicle';
import { versionHeaderSchema } from './version';

/** プロジェクト本体。 */
export const projectBodySchema = z
  .object({
    id: documentIdSchema,
    name: nameSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    courses: z.array(courseSchema).max(LIMITS.maxCourses),
    /** ユーザー定義の車両（組み込みは含めない） */
    vehicles: z.array(vehicleSchema).default([]),
    /** ユーザー定義の環境（組み込みは含めない） */
    environments: z.array(environmentSchema).default([]),
  })
  .superRefine((project, context) => {
    const seen = new Set<string>();
    project.courses.forEach((course, index) => {
      if (seen.has(course.id)) {
        context.addIssue({
          code: 'custom',
          path: ['courses', index, 'id'],
          message: `コースの ID が重複しています: ${course.id}`,
        });
      }
      seen.add(course.id);
    });
    const environmentIds = new Set<string>();
    project.environments.forEach((environment, index) => {
      if (environment.id.startsWith('builtin:')) {
        context.addIssue({
          code: 'custom',
          path: ['environments', index, 'id'],
          message: 'ユーザー定義の環境に builtin: の ID は使えません',
        });
      }
      if (environmentIds.has(environment.id)) {
        context.addIssue({
          code: 'custom',
          path: ['environments', index, 'id'],
          message: `環境の ID が重複しています: ${environment.id}`,
        });
      }
      environmentIds.add(environment.id);
    });
    const vehicleIds = new Set<string>();
    project.vehicles.forEach((vehicle, index) => {
      if (vehicle.id.startsWith('builtin:')) {
        context.addIssue({
          code: 'custom',
          path: ['vehicles', index, 'id'],
          message: 'ユーザー定義の車両に builtin: の ID は使えません',
        });
      }
      if (vehicleIds.has(vehicle.id)) {
        context.addIssue({
          code: 'custom',
          path: ['vehicles', index, 'id'],
          message: `車両の ID が重複しています: ${vehicle.id}`,
        });
      }
      vehicleIds.add(vehicle.id);
    });
  });

/** プロジェクトファイル（`.rcs.json`）。 */
export const projectFileSchema = versionHeaderSchema.extend({
  kind: z.literal('rcs.project'),
  project: projectBodySchema,
});

/** プロジェクト本体。 */
export type Project = z.infer<typeof projectBodySchema>;

/** プロジェクトファイル（版情報つき）。 */
export type ProjectFile = z.infer<typeof projectFileSchema>;
